import { createI18n } from 'vue-i18n';

import en from './locales/en.json';
import ru from './locales/ru.json';

/**
 * Конфигурация vue-i18n.
 *
 * Сейчас основная локаль — `ru`. `en` заведён сразу, но переводы пустые
 * (или равны русским) — их заполнят, когда понадобится английская версия.
 *
 * Использование в компонентах:
 *   <script setup>import { useI18n } from 'vue-i18n'; const { t } = useI18n();</script>
 *   <template>{{ t('dashboard.title') }}</template>
 */
/**
 * Форматы дат для `d()`.
 *
 * Без них `d(date, 'short')` возвращает пустую строку — молча, без ошибки.
 * Из-за этого на странице участия расписание, питание и отметки о проходах
 * выводились вообще без времени: место под него было, а времени в нём не
 * было.
 *
 * `dayTime` — день и время без года: расписание всегда внутри одного
 * мероприятия, год в каждой строке был бы шумом.
 */
const datetimeFormats = {
  short: {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  },
  time: { hour: '2-digit', minute: '2-digit' },
  dayTime: {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  },
  date: { day: 'numeric', month: 'long', year: 'numeric' },
  weekday: { weekday: 'long', day: 'numeric', month: 'long' },
} as const;

/**
 * Русские склонения числительных.
 *
 * Без своего правила vue-i18n считает по английскому: «одна форма или много»,
 * и из трёх записанных вариантов берёт вторую при любом числе, кроме единицы.
 * Отсюда «через 5 дня» и «разрыв 33 пункта» — сообщения написаны правильно, а
 * выбиралась не та форма.
 *
 * Порядок форм в сообщении: 1 | 2–4 | 5–20.
 */
function russianPlural(choice: number, choicesLength: number): number {
  if (choicesLength < 3) return choice === 1 ? 0 : 1;
  const abs = Math.abs(choice);
  const tens = abs % 100;
  const ones = abs % 10;
  if (tens > 10 && tens < 20) return 2;
  if (ones === 1) return 0;
  if (ones >= 2 && ones <= 4) return 1;
  return 2;
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: 'ru',
  fallbackLocale: 'ru',
  messages: { ru, en },
  datetimeFormats: { ru: datetimeFormats, en: datetimeFormats },
  pluralRules: { ru: russianPlural },
});

export type Locale = 'ru' | 'en';
